import { ChevronLeft, ChevronRight, ChevronsLeft } from "@gravity-ui/icons";
import { Button, Icon, Select, Spin, Text } from "@gravity-ui/uikit";
import { useAction, useAtom } from "@reatom/npm-react";
import { useCallback, useEffect } from "react";
import { pluralNumber } from "src/util";

import { useProductPageTranslate } from "../../hooks";
import {
  currentPerPageAtom,
  setCurrentPerPage,
  cursorHistoryLengthAtom,
} from "../../store/atoms/cursorPagination";
import {
  searchProductCountStore,
  searchProductStore,
} from "../../store/fetchers";
import { PAGINATION_OPTIONS } from "./Pagination.constants";
import css from "./Pagination.module.scss";

interface ProductPaginationProps {
  isTable?: boolean;
}

export const ProductPagination = ({ isTable }: ProductPaginationProps) => {
  const { productsCountFound } = useProductPageTranslate();
  const [historyLength] = useAtom(cursorHistoryLengthAtom);
  const [currentPerPage] = useAtom(currentPerPageAtom);
  const [hasNext] = useAtom(searchProductStore.hasNext);
  const [isLoading] = useAtom(searchProductStore.isPaginationLoading);
  const [productsCount] = useAtom(searchProductCountStore.productsCountAtom);
  const [isCountLoading] = useAtom(
    searchProductCountStore.isProductsCountLoading,
  );
  const loadNextPage = useAction(searchProductStore.loadNextPage);
  const loadPreviousPage = useAction(searchProductStore.loadPreviousPage);
  const updateProducts = useAction(searchProductStore.updateProducts);
  const resetToFirstPage = useAction(searchProductStore.resetToFirstPage);
  const setPerPage = useAction(setCurrentPerPage);
  const fetchProductsCount = useAction(
    searchProductCountStore.fetchProductsCount,
  );

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- загрузка счётчика товаров при показе таблицы (data fetch)
    if (isTable) {
      fetchProductsCount();
    }
  }, [isTable, fetchProductsCount]);

  const canGoNext = hasNext;
  const canGoPrevious = historyLength > 0;

  const onResetToFirstPage = useCallback(() => {
    resetToFirstPage();
  }, [resetToFirstPage]);

  const onLoadPreviousPage = useCallback(() => {
    loadPreviousPage();
  }, [loadPreviousPage]);

  const onLoadNextPage = useCallback(() => {
    loadNextPage();
  }, [loadNextPage]);

  const onUpdatePerPage = useCallback(
    (pageSize: number) => {
      setPerPage(pageSize);
      updateProducts();
    },
    [setPerPage, updateProducts],
  );

  // Не используем Gravity Pagination, так как у нас курсорная пагинация без общего количества элементов
  // Gravity Pagination требует total для корректной работы, что недоступно в нашем случае
  if (!isTable) {
    return null;
  }

  const productsCountText =
    productsCount !== null
      ? `${productsCountFound} ${productsCount} ${pluralNumber(productsCount, [
          "товар",
          "товара",
          "товаров",
        ])}`
      : null;

  return (
    <div className={css.paginationWrapper}>
      <div className={css.paginationGroup}>
        <div className={css.pagination}>
          <Button
            size="l"
            disabled={!canGoPrevious}
            view="outlined"
            onClick={onResetToFirstPage}
            loading={isLoading}
          >
            <Icon data={ChevronsLeft} />
          </Button>

          <Button
            size="l"
            disabled={!canGoPrevious}
            view="outlined"
            onClick={onLoadPreviousPage}
            loading={isLoading}
          >
            <Icon data={ChevronLeft} />
          </Button>

          <Button size="l" view="action">
            {historyLength + 1}
          </Button>

          <Button
            size="l"
            disabled={!canGoNext}
            view="outlined"
            onClick={onLoadNextPage}
            loading={isLoading}
          >
            <Icon data={ChevronRight} />
          </Button>

          <Select
            size="l"
            value={[currentPerPage.toString()]}
            loading={isLoading}
            onUpdate={(value) => onUpdatePerPage(parseInt(value[0]))}
            options={PAGINATION_OPTIONS}
          />
        </div>

        <div className={css.productsCount}>
          {isCountLoading ? (
            <Spin size="s" className={css.countSpin} />
          ) : productsCountText ? (
            <Text variant="body-2" color="secondary">
              {productsCountText}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
};
